import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";

interface User {
  id: string;
  email: string;
  name: string;
  isSubscribed: boolean;
  roles: string[];
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithOAuth: (provider: "google" | "github") => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuthStatus = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const response = await api.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error("[AuthContext] Auth check failed:", error);
        localStorage.removeItem("access_token");
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setUser(null);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    const handleAuthChange = () => {
      checkAuthStatus();
    };
    window.addEventListener("customAuthChange", handleAuthChange);
    return () =>
      window.removeEventListener("customAuthChange", handleAuthChange);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post("/api/auth/login", { email, password });

      // Backend returns { success: true, token: "...", user: {...} }
      const { token } = response.data;

      if (!token) {
        throw new Error("Login failed: Invalid response from server");
      }

      localStorage.setItem("access_token", token);

      // After setting the token, we re-use the trusted checkAuthStatus
      // function to fetch the user's identity and set the state.
      // Awaiting this ensures the user state is set before navigation occurs.
      await checkAuthStatus();
    } catch (error) {
      console.error("[AuthContext] Login failed:", error);
      localStorage.removeItem("access_token");
      setUser(null);
      throw error;
    }
  };

  const loginWithOAuth = async (provider: "google" | "github") => {
    setIsLoading(true);
    try {
      const baseUrl =
        import.meta.env.VITE_API_URL ||
        (import.meta.env.DEV
          ? "http://localhost:4000"
          : "https://api.expedient.one");
      window.location.href = `${baseUrl}/api/auth/${provider}`;
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    setUser(null);
    window.dispatchEvent(new CustomEvent("customAuthChange"));
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, loginWithOAuth, logout, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
